import CaptureProcessingReceipt from './CaptureProcessingReceipt';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChevronRightRounded as ChevronRightRoundedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import {
  canProcessCareSourceNow,
  formatCareSourceRecordedAt,
  formatCareSourceTimelineDateSummary,
  getCareSourceFailureReason,
  getCareSourceKnowledgeSuggestions,
  getCareSourceDisplayStatus,
  getCareSourceContext,
  getCareSourceOriginalText,
  getCareSourceProfileSuggestions,
  getCareSourceProcessActionLabel,
  getCareSourceReviewTarget,
  getCareSourceSavedRecordCount,
  getCareSourceTimelinePointerCount,
  getCareSourceTitle,
  hasCareSourceSavedTimelineRecords,
} from './careSourceStatusModel';
import {
  dismissCareSourceActivity,
  saveCareSourceAsTimelineNote,
} from './careSourceActivityService';
import { organizeCapturedCareSource } from './careSourceProcessingClient';
import CareKnowledgeSuggestionReview from './CareKnowledgeSuggestionReview';
import MedicationSourceReview from './MedicationSourceReview';
import { hasMedicationSourceSuggestions } from './medicationSourceSuggestions';
import CareSourceReviewableCandidateActions from './CareSourceReviewableCandidateActions';
import {
  buildReviewableCandidatesFromCareSource,
  splitReviewableCareKnowledgeSuggestions,
} from './careSourceReviewableCandidates';
import { buildExtractionResultSummary } from './extractionResultSummaryModel';
import ExtractionResultSummary from './ExtractionResultSummary';
import ImportHistoryReviewSheet from './ImportHistoryReviewSheet';

const hasPersistedMedicationSuggestions = (source = {}) => (
  (Array.isArray(source.suggestedCareKnowledgeItems) ? source.suggestedCareKnowledgeItems : [])
    .some((suggestion) => (
      String(suggestion?.knowledgeType || '').trim().toLowerCase() === 'medication'
      && suggestion?.needsReview !== false
      && String(suggestion?.reviewState || '').trim().toLowerCase() !== 'approved'
      && String(suggestion?.reviewState || '').trim().toLowerCase() !== 'ignored'
      && suggestion?.hidden !== true
      && suggestion?.archived !== true
    ))
);

const ATTENTION_INK = '#A86A00';
const ATTENTION_FILL = '#D08A00';
const CARE_KNOWLEDGE_NAV_CONTEXTS = new Set(['knowledge', 'profile', 'goal']);

const pluralize = (count = 0, singular = '', plural = `${singular}s`) => (
  `${count} ${count === 1 ? singular : plural}`
);

const getReviewCounts = (source = {}) => {
  const careKnowledgeCount = Number(
    source.careKnowledgeSuggestionSummary?.itemCount
    || source.suggestedCareKnowledgeItems?.length
    || 0
  );
  const profileCount = Number(
    source.profileSuggestionSummary?.suggestionCount
    || getCareSourceProfileSuggestions(source).reduce((total, group) => total + group.items.length, 0)
    || 0
  );
  const momentCount = getCareSourceSavedRecordCount(source) + getCareSourceTimelinePointerCount(source);

  return {
    factCount: careKnowledgeCount + profileCount,
    momentCount,
  };
};

const buildReviewCountText = (source = {}) => {
  const { factCount, momentCount } = getReviewCounts(source);
  const parts = [];

  if (factCount > 0) parts.push(pluralize(factCount, 'fact'));
  if (momentCount > 0) parts.push(pluralize(momentCount, 'moment'));

  return parts.length ? `${parts.join(' · ')} found` : 'Review extracted items';
};

const CareSourceStatusRow = ({
  source = {},
  existingMedications = [],
  onReviewTimeline,
  onAddMedication,
  onEditMedication,
  onMedicationListChanged,
  onMedicationReviewComplete,
  onSetReminder,
  medicationReviewNavigation = null,
  showDismiss = false,
  showSummary = true,
  defaultReviewingMedications = false,
  hideMedicationReviewToggle = false,
  hideCareKnowledgeNavigation = false,
  careKnowledgeReviewMode = 'inline',
  onProfileReviewComplete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [savingOriginal, setSavingOriginal] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [reviewingMedications, setReviewingMedications] = useState(defaultReviewingMedications);
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [reviewingImportHistory, setReviewingImportHistory] = useState(false);
  const [processError, setProcessError] = useState('');
  const status = getCareSourceDisplayStatus(source);
  const sourceContext = getCareSourceContext(source);
  const originalText = getCareSourceOriginalText(source);
  const title = getCareSourceTitle(source);
  const recordedAtLabel = formatCareSourceRecordedAt(source);
  const timelineDateSummaryLabel = formatCareSourceTimelineDateSummary(source);
  const canProcess = canProcessCareSourceNow(source);
  const actionLabel = getCareSourceProcessActionLabel(source);
  const canReviewTimeline = hasCareSourceSavedTimelineRecords(source);
  const reviewTimelineTarget = canReviewTimeline ? getCareSourceReviewTarget(source) : null;
  const failureReason = getCareSourceFailureReason(source);
  const canSaveOriginalAsTimelineNote = Boolean(canProcess && originalText);
  const canDismiss = showDismiss && (status.label === 'Failed' || status.label.startsWith('Saved'));
  const hasMedicationSuggestions = hasMedicationSourceSuggestions(source);
  const hasCareKnowledgeSuggestions = getCareSourceKnowledgeSuggestions(source).length > 0
    && (
      source.careKnowledgeSuggestionSummary?.needsReview !== false
      || !source.careKnowledgeSavedItems?.length
    )
    && source.reviewState !== 'reviewed';
  const reviewableCandidates = buildReviewableCandidatesFromCareSource(source);
  const importHistoryCandidateCount = Number(source.pendingTimelineReview?.candidates?.length || 0);
  const canReviewImportHistory = importHistoryCandidateCount > 0;
  const hasPersistedDocumentFindings = Boolean(
    source.documentFindings
    || source.metadata?.careSourceProcessing?.documentFindings
  );
  const hasGenericKnowledgeSuggestions = splitReviewableCareKnowledgeSuggestions(source).generic.length > 0;
  const showStandaloneOriginalNote = Boolean(
    originalText
    && !(careKnowledgeReviewMode === 'inline' && hasCareKnowledgeSuggestions)
  );
  const hasProfileSuggestions = getCareSourceProfileSuggestions(source).length > 0
    && source.profileSuggestionSummary?.needsReview !== false
    && source.reviewState !== 'reviewed';
  const shouldLinkCareKnowledgeReview = (hasCareKnowledgeSuggestions || hasProfileSuggestions)
    && careKnowledgeReviewMode === 'link'
    && CARE_KNOWLEDGE_NAV_CONTEXTS.has(sourceContext);
  const isCareKnowledgePage = typeof window !== 'undefined'
    && window.location?.pathname === '/care-knowledge';
  const shouldLinkSavedCareDocument = Boolean(
    source.id
    && source.childId
    && !hideCareKnowledgeNavigation
    && !isCareKnowledgePage
    && !shouldLinkCareKnowledgeReview
    && !canReviewTimeline
    && CARE_KNOWLEDGE_NAV_CONTEXTS.has(sourceContext)
  );
  const hasPersistedMedicationReview = hasPersistedMedicationSuggestions(source);
  const canReviewMedications = (sourceContext === 'medication' || hasMedicationSuggestions || hasPersistedMedicationReview)
    && status.label !== 'Reviewed'
    && (status.label === 'Needs your review' || hasPersistedMedicationReview)
    && Boolean(hasMedicationSuggestions || originalText || hasPersistedMedicationReview);
  const canReviewStructuredCandidates = Boolean(
    reviewableCandidates.length > 0
    && source.reviewState !== 'reviewed'
    && hasPersistedDocumentFindings
    && !shouldLinkCareKnowledgeReview
  );
  const hasActions = Boolean(
    originalText
    || canReviewTimeline
    || canProcess
    || canReviewMedications
    || canReviewStructuredCandidates
    || canReviewImportHistory
    || hasCareKnowledgeSuggestions
    || hasProfileSuggestions
    || shouldLinkSavedCareDocument
    || canDismiss
  );
  const useSplitInlineActions = Boolean(
    canReviewTimeline
    && originalText
    && !canReviewMedications
    && !canProcess
    && !canDismiss
  );
  const shouldRenderCareKnowledgeReviewCard = Boolean(shouldLinkCareKnowledgeReview);
  const extractionSummary = buildExtractionResultSummary(source);
  const extractionOwnerName = source.childName || 'this person';

  const handleProcessNow = async () => {
    if (!source.id || processing) return;

    setProcessing(true);
    setProcessError('');
    try {
      const result = await organizeCapturedCareSource({
        sourceNoteId: source.id,
        options: {
          force: true,
        },
      });

      if (result?.ok === false) {
        throw new Error(result?.error?.message || "We couldn't start processing this source.");
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
          detail: {
            childId: source.childId || null,
            sourceNoteId: source.id,
            collection: 'dailyLogs',
          },
        }));
      }
    } catch (error) {
      setProcessError(error?.message || "We couldn't start processing this source.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDismiss = async () => {
    if (!source.id || dismissing) return;

    setDismissing(true);
    setProcessError('');
    try {
      await dismissCareSourceActivity(source.id);
    } catch (error) {
      setProcessError(error?.message || "We couldn't hide this source from activity.");
      setDismissing(false);
    }
  };

  const handleSaveOriginalAsTimelineNote = async () => {
    if (!source.id || savingOriginal) return;

    setSavingOriginal(true);
    setProcessError('');
    try {
      const result = await saveCareSourceAsTimelineNote(source);
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
          detail: {
            childId: source.childId || null,
            sourceNoteId: source.id,
            entryId: result?.entryId || null,
            collection: 'dailyLogs',
          },
        }));
      }
    } catch (error) {
      setProcessError(error?.message || "We couldn't save this note to the timeline.");
    } finally {
      setSavingOriginal(false);
    }
  };

  if (shouldRenderCareKnowledgeReviewCard) {
    return (
      <Box sx={{ py: { xs: 1.25, sm: 1.4 } }}>
        <Box
          sx={{
            border: `1.5px solid ${alpha(ATTENTION_FILL, 0.34)}`,
            borderRadius: '18px',
            bgcolor: alpha('#FFFFFF', 0.96),
            p: { xs: 1, sm: 1.15 },
          }}
        >
          <Stack spacing={0.9}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '0.98rem' },
                    fontWeight: 950,
                    color: colors.semantic.ink,
                    lineHeight: 1.1,
                  }}
                >
                  Needs your review
                </Typography>
                <Box
                  component="span"
                  sx={{
                    minHeight: 24,
                    px: 0.75,
                    py: 0.25,
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: '999px',
                    border: `1px solid ${alpha(ATTENTION_FILL, 0.34)}`,
                    bgcolor: alpha(ATTENTION_FILL, 0.12),
                    color: ATTENTION_INK,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                  }}
                >
                  Ready
                </Box>
              </Stack>
              <Typography
                sx={{
                  color: alpha(colors.semantic.ink, 0.42),
                  fontSize: { xs: '0.74rem', sm: '0.8rem' },
                  fontWeight: 650,
                  whiteSpace: 'nowrap',
                }}
              >
                document saved
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 52, sm: 56 },
                  height: { xs: 52, sm: 56 },
                  borderRadius: '16px',
                  bgcolor: alpha(ATTENTION_FILL, 0.16),
                  color: ATTENTION_INK,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                sx={{
                    fontSize: { xs: '0.9rem', sm: '0.98rem' },
                    fontWeight: 950,
                    color: colors.semantic.ink,
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.2,
                    color: alpha(colors.semantic.ink, 0.6),
                    fontSize: { xs: '0.78rem', sm: '0.82rem' },
                    fontWeight: 650,
                    lineHeight: 1.25,
                  }}
                >
                  {buildReviewCountText(source)}
                </Typography>
              </Box>
            </Stack>

            <Button
              component={RouterLink}
              to="/care-knowledge"
              state={{
                childId: source.childId || '',
                sourceId: source.id || '',
              }}
              variant="contained"
              endIcon={<ChevronRightRoundedIcon />}
              sx={{
                alignSelf: 'flex-start',
                minHeight: { xs: 34, sm: 36 },
                borderRadius: '999px',
                px: { xs: 1.2, sm: 1.35 },
                bgcolor: colors.semantic.ink,
                color: '#FFFFFF',
                fontSize: { xs: '0.78rem', sm: '0.82rem' },
                fontWeight: 850,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: colors.brand.ink,
                  boxShadow: 'none',
                },
              }}
            >
              View details
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 1.05, sm: 1.2 } }}>
      <Stack spacing={0.55}>
        {showSummary ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '0.84rem',
                  fontWeight: 700,
                  color: colors.semantic.ink,
                  wordBreak: 'break-word',
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha(colors.semantic.ink, 0.68),
                  fontSize: '0.8rem',
                  lineHeight: 1.45,
                }}
              >
                {status.detail}
              </Typography>
              {recordedAtLabel ? (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.35,
                    color: alpha(colors.semantic.ink, 0.58),
                    fontSize: '0.76rem',
                    fontWeight: 750,
                    lineHeight: 1.35,
                  }}
                >
                  Source saved {recordedAtLabel}
                </Typography>
              ) : null}
              {timelineDateSummaryLabel ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onReviewTimeline?.(reviewTimelineTarget)}
                  disabled={!canReviewTimeline}
                  sx={{
                    mt: 0.3,
                    minHeight: 0,
                    justifyContent: 'flex-start',
                    p: 0,
                    color: colors.semantic.profileFlow.completionText,
                    fontSize: '0.76rem',
                    fontWeight: 750,
                    lineHeight: 1.35,
                    textAlign: 'left',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: colors.brand.ink,
                      textDecoration: canReviewTimeline ? 'underline' : 'none',
                    },
                    '&.Mui-disabled': {
                      color: colors.semantic.profileFlow.completionText,
                    },
                  }}
                >
                  {timelineDateSummaryLabel}
                </Button>
              ) : null}
            </Box>
          </Stack>
        ) : null}

        {careKnowledgeReviewMode === 'inline' && extractionSummary.total > 0 ? (
          <ExtractionResultSummary
            source={source}
            summary={extractionSummary}
            personName={extractionOwnerName}
            onReview={() => {
              setReviewExpanded(true);
              if (hasMedicationSuggestions) setReviewingMedications(true);
            }}
          />
        ) : null}

        {hasActions ? (
          <Box>
            <Stack
              direction="row"
              spacing={useSplitInlineActions ? 0 : { xs: 1.5, sm: 2 }}
              alignItems="center"
              justifyContent={useSplitInlineActions ? 'space-between' : 'flex-start'}
              sx={{
                width: '100%',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                '& .source-action-button': {
                  minHeight: { xs: 34, sm: 36 },
                  borderRadius: '999px',
                  fontWeight: 850,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                },
              }}
            >
              {canReviewMedications && !hideMedicationReviewToggle ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="contained"
                  onClick={() => setReviewingMedications((current) => !current)}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.77rem', sm: '0.8125rem' },
                    bgcolor: colors.brand.deep,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: colors.brand.ink,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {reviewingMedications
                    ? 'Hide medication review'
                    : hasMedicationSuggestions
                      ? 'Review medication details'
                      : 'Review medication source'}
                </Button>
              ) : null}
              {canReviewImportHistory ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="contained"
                  onClick={() => setReviewingImportHistory(true)}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.77rem', sm: '0.8125rem' },
                    bgcolor: colors.brand.deep,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: colors.brand.ink, boxShadow: 'none' },
                  }}
                >
                  Review {importHistoryCandidateCount} historical {importHistoryCandidateCount === 1 ? 'entry' : 'entries'}
                </Button>
              ) : null}
              {canReviewTimeline ? (
                <Button
                  className="source-inline-action"
                  size="small"
                  variant="text"
                  onClick={() => onReviewTimeline?.(reviewTimelineTarget)}
                  sx={{
                    minHeight: { xs: 30, sm: 32 },
                    px: { xs: 0.1, sm: 0.15 },
                    fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                    color: colors.brand.deep,
                    fontWeight: 750,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: colors.brand.ink,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  See saved care details
                </Button>
              ) : null}
              {shouldLinkCareKnowledgeReview ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="contained"
                  component={RouterLink}
                  to="/care-knowledge"
                  state={{
                    childId: source.childId || '',
                    sourceId: source.id || '',
                  }}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.77rem', sm: '0.8125rem' },
                    bgcolor: colors.brand.deep,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: colors.brand.ink,
                      boxShadow: 'none',
                    },
                  }}
                >
                  Review extracted items
                </Button>
              ) : null}
              {canReviewStructuredCandidates ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="contained"
                  onClick={() => setReviewExpanded((current) => !current)}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.77rem', sm: '0.8125rem' },
                    bgcolor: colors.brand.deep,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: colors.brand.ink,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {reviewExpanded ? 'Hide found items' : 'Review found items'}
                </Button>
              ) : null}
              {shouldLinkSavedCareDocument ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="contained"
                  component={RouterLink}
                  to="/care-knowledge"
                  state={{
                    childId: source.childId || '',
                    sourceId: source.id || '',
                  }}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.77rem', sm: '0.8125rem' },
                    bgcolor: colors.brand.deep,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: colors.brand.ink,
                      boxShadow: 'none',
                    },
                  }}
                >
                  Open in Care Knowledge
                </Button>
              ) : null}
              {showStandaloneOriginalNote ? (
                <Button
                  className="source-inline-action"
                  size="small"
                  variant="text"
                  onClick={() => setExpanded((current) => !current)}
                  sx={{
                    minHeight: { xs: 30, sm: 32 },
                    px: { xs: 0.1, sm: 0.15 },
                    fontSize: { xs: '0.77rem', sm: '0.8125rem' },
                    color: colors.brand.deep,
                    fontWeight: 750,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: colors.brand.ink,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {expanded ? 'Hide note' : 'View note'}
                </Button>
              ) : null}
              {canProcess ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="outlined"
                  onClick={handleProcessNow}
                  disabled={processing}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                  }}
                >
                  {processing ? 'Trying again...' : actionLabel || 'Try again'}
                </Button>
              ) : null}
              {canSaveOriginalAsTimelineNote ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="outlined"
                  onClick={handleSaveOriginalAsTimelineNote}
                  disabled={savingOriginal}
                  sx={{
                    px: { xs: 1, sm: 1.45 },
                    fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                  }}
                >
                  {savingOriginal ? 'Saving...' : 'Save as one timeline note'}
                </Button>
              ) : null}
              {canDismiss ? (
                <Button
                  className="source-action-button"
                  size="small"
                  variant="text"
                  onClick={handleDismiss}
                  disabled={dismissing}
                  sx={{
                    minWidth: { sm: 0 },
                    px: { xs: 0.45, sm: 0.35 },
                    fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                    color: alpha(colors.semantic.ink, 0.62),
                  }}
                >
                  {dismissing ? 'Hiding...' : 'Dismiss'}
                </Button>
              ) : null}
            </Stack>
            <Collapse in={expanded && showStandaloneOriginalNote} unmountOnExit>
              <Box
                sx={{
                  mt: 1,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    m: 0,
                    fontFamily: 'inherit',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {originalText}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        ) : null}

        <MedicationSourceReview
          source={source}
          existingMedications={existingMedications}
          open={reviewingMedications}
          onError={setProcessError}
          onAddMedication={onAddMedication}
          onEditMedication={onEditMedication}
          onMedicationListChanged={onMedicationListChanged}
          onReviewComplete={onMedicationReviewComplete}
          onSetReminder={onSetReminder}
          medicationReviewNavigation={medicationReviewNavigation}
        />

        <ImportHistoryReviewSheet
          source={source}
          existingMedications={existingMedications}
          onMedicationListChanged={onMedicationListChanged}
          open={reviewingImportHistory}
          onClose={() => setReviewingImportHistory(false)}
        />

        {careKnowledgeReviewMode === 'inline' || canReviewStructuredCandidates ? (
          <Collapse in={reviewExpanded} timeout="auto">
            <Stack spacing={1}>
              {reviewableCandidates.length ? (
                <CareSourceReviewableCandidateActions
                  source={source}
                  hasGenericSuggestions={hasGenericKnowledgeSuggestions}
                  onError={setProcessError}
                />
              ) : null}

              {hasCareKnowledgeSuggestions ? (
                <CareKnowledgeSuggestionReview
                  source={source}
                  onError={setProcessError}
                  onProfileReviewComplete={onProfileReviewComplete}
                />
              ) : null}
            </Stack>
          </Collapse>
        ) : null}

        <CaptureProcessingReceipt source={source} />
        {processError ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {processError}
          </Alert>
        ) : null}

        {failureReason ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {failureReason}
          </Alert>
        ) : null}
      </Stack>
    </Box>
  );
};

export default CareSourceStatusRow;
